import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api.js';

class ApiService {
  // Authentication API (PHP endpoints)
  async login(credentials) {
    const response = await apiPost('/auth/login', credentials);
    if (response.data && response.data.user && response.data.token) {
      localStorage.setItem('auth_token', response.data.token);
      localStorage.setItem('user_data', JSON.stringify(response.data.user));
      const expires = new Date().getTime() + (24 * 60 * 60 * 1000); // 24 hours
      const sessionData = {
        userId: response.data.user.id,
        user: response.data.user,
        expires: expires,
        loginTime: new Date().toISOString()
      };
      localStorage.setItem('digital_id_session', JSON.stringify(sessionData));
      
      return response.data.user;
    }
    throw new Error(response.message || 'Login failed');
  }

  async register(userData) {
    const response = await apiPost('/auth/register', userData);
    // PHP response format: { data: { user, token }, message, success }
    if (response.data && response.data.user && response.data.token) {
      // Store token for API requests
      localStorage.setItem('auth_token', response.data.token);
      localStorage.setItem('user_data', JSON.stringify(response.data.user));
      
      // ALSO store in session format for auth.js compatibility
      const expires = new Date().getTime() + (24 * 60 * 60 * 1000); // 24 hours
      const sessionData = {
        userId: response.data.user.id,
        user: response.data.user,
        expires: expires,
        loginTime: new Date().toISOString()
      };
      localStorage.setItem('digital_id_session', JSON.stringify(sessionData));
      
      return response.data.user;
    }
    throw new Error(response.message || 'Registration failed');
  }

  async logout() {
    const response = await apiPost('/auth/logout');
    // Clear all auth data
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('digital_id_session');
    return response.data;
  }

  async getCurrentUser() {
    const token = localStorage.getItem('auth_token');
    if (!token) return null;
    
    try {
      const payload = JSON.parse(atob(token));
      return payload.data;
    } catch {
      return null;
    }
  }

  // Users API (PHP endpoints)
  async getUsers() {
    const response = await apiGet('/users');
    return response.data;
  }

  async getUserById(id) {
    const response = await apiGet(`/users/${id}`);
    return response.data;
  }

  async createUser(userData) {
    const response = await apiPost('/users', userData);
    return response.data;
  }

  async updateUser(id, userData) {
    console.log('apiService.updateUser - ID:', id);
    console.log('apiService.updateUser - Data:', userData);
    console.log('apiService.updateUser - Token:', localStorage.getItem('auth_token')?.substring(0, 50) + '...');
    const response = await apiPut(`/users/${id}`, userData);
    console.log('apiService.updateUser - Response:', response);
    return response.data;
  }

  async deleteUser(id) {
    const response = await apiDelete(`/users/${id}`);
    return response.data;
  }

  // Digital Cards API (PHP endpoints)
  async getDigitalCards() {
    try {
      console.log('🔍 Fetching digital cards...');
      const response = await apiGet('/digital-cards');
      console.log('📦 Full response:', response);
      
      if (response && response.data !== undefined) {
        console.log('✅ Cards from response.data:', response.data);
        return response.data;
      }
      if (Array.isArray(response)) {
        console.log('✅ Cards from array response:', response);
        return response;
      }
      
      console.warn('⚠️ Unexpected response format:', response);
      return [];
    } catch (error) {
      console.error('❌ Error fetching cards:', error);
      return [];
    }
  }

  async getDigitalCardById(id) {
    const response = await apiGet(`/digital-cards/${id}`);
    return response.data;
  }

  async createDigitalCard(cardData) {
    const response = await apiPost('/digital-cards', cardData);
    return response.data;
  }

  async updateDigitalCard(id, cardData) {
    
    const response = await apiPut(`/digital-cards/${id}`, cardData);
    
    if (!response || !response.success) {
      console.error('❌ apiService: Update failed! Response:', response);
      throw new Error(response?.error || 'Update failed - no success response');
    }
    
    return response.data;
  }

  async deleteDigitalCard(id) {
    const response = await apiDelete(`/digital-cards/${id}`);
    return response.data;
  }

  async generateShareToken(id) {
    return this.getDigitalCardById(id);
  }

  async getSharedCard(token) {
    return null;
  }

  // Dashboard API (simple stats)
  async getDashboardStats() {
    try {
      const cards = await this.getDigitalCards();
      const users = await this.getUsers();
      
      return {
        total_cards: cards.length,
        published_cards: cards.filter(c => c.status === 'published').length,
        draft_cards: cards.filter(c => c.status === 'draft').length,
        total_users: users.length
      };
    } catch (error) {
      return {
        total_cards: 0,
        published_cards: 0,
        draft_cards: 0,
        total_users: 0
      };
    }
  }

  // Legacy compatibility methods
  async authenticateUser(username, password) {
    return this.login({ username, password });
  }

  async getPublishedCards() {
    const cards = await this.getDigitalCards();
    return cards.filter(card => card.status === 'published');
  }

  async initializeDatabase() {
    const response = await apiPost('/init');
    return response.data;
  }

  async generateShortlink(cardId) {
    const response = await apiPost('/share', { card_id: cardId });
    return response.data;
  }

  async resolveShortlink(shortCode) {
    const response = await apiGet(`/share/${shortCode}`);
    return response.data;
  }
}

const apiService = new ApiService();
export default apiService;